import { useEffect, useRef, useState } from 'react';

export type AnimVariant = 'fadeUp' | 'fadeLeft' | 'fadeRight' | 'scaleIn' | 'clipReveal' | 'fadeIn';

interface ScrollAnimOptions {
  threshold?: number;
  variant?: AnimVariant;
}

const hiddenStyles: Record<AnimVariant, string> = {
  fadeUp:      'opacity-0 translate-y-10',
  fadeLeft:    'opacity-0 -translate-x-10',
  fadeRight:   'opacity-0 translate-x-10',
  scaleIn:     'opacity-0 scale-90',
  clipReveal:  'opacity-0 translate-y-6 scale-[0.97]',
  fadeIn:      'opacity-0',
};

const visibleStyles: Record<AnimVariant, string> = {
  fadeUp:      'opacity-100 translate-y-0',
  fadeLeft:    'opacity-100 translate-x-0',
  fadeRight:   'opacity-100 translate-x-0',
  scaleIn:     'opacity-100 scale-100',
  clipReveal:  'opacity-100 translate-y-0 scale-100',
  fadeIn:      'opacity-100',
};

export function useScrollAnimation(options: ScrollAnimOptions = {}) {
  const { threshold = 0.06, variant = 'fadeUp' } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px 80px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  const animClass = `transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
    isVisible ? visibleStyles[variant] : hiddenStyles[variant]
  }`;

  return { ref, isVisible, animClass };
}
